import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { cn } from "@/lib/utils";
import type { SdrStageEntry, SdrStageKey } from "@/data/types";

type Period = { periodStart: string; periodEnd: string };

const STAGE_CONFIG: Record<
  SdrStageKey,
  { title: string; subtitle: string; chip: string; accent: string }
> = {
  leads: {
    title: "Leads",
    subtitle: "Contatos entrantes no período — ordenados por mais tempo parado (ação urgente no topo)",
    chip: "bg-chart-4/15 text-chart-4",
    accent: "hsl(var(--chart-4))",
  },
  connections: {
    title: "Conexões",
    subtitle: "Contatos com resposta confirmada pela equipe de SDR",
    chip: "bg-chart-3/15 text-chart-3",
    accent: "hsl(var(--chart-3))",
  },
  meetingsScheduled: {
    title: "Reuniões agendadas",
    subtitle: "Calendário confirmado — ordenado pela reunião mais próxima",
    chip: "bg-chart-1/15 text-chart-1",
    accent: "hsl(var(--chart-1))",
  },
  meetingsHeld: {
    title: "Reuniões realizadas",
    subtitle: "Closer já assumiu o lead — ordenado por mais recente",
    chip: "bg-brand-amber/15 text-brand-amber",
    accent: "hsl(var(--brand-amber))",
  },
};

const PAGE_SIZE = 7;

export function SdrStageSection({
  stage,
  period,
  onClear,
}: {
  stage: SdrStageKey | null;
  period: Period;
  onClear: () => void;
}) {
  const [search, setSearch] = useState("");
  const [sdrFilter, setSdrFilter] = useState<string>("all");
  const [page, setPage] = useState(1);

  const q = useQuery({
    queryKey: ["sdr-stage", stage, period],
    queryFn: () => client.getSdrStageDetail({ stage: stage!, ...period }),
    enabled: !!stage,
  });

  useEffect(() => {
    setSearch("");
    setSdrFilter("all");
    setPage(1);
  }, [stage]);

  useEffect(() => {
    setPage(1);
  }, [search, sdrFilter]);

  const detail = q.data;

  const sdrOptions = useMemo(() => {
    const set = new Set<string>();
    (detail?.entries ?? []).forEach((e) => set.add(e.sdrName));
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [detail]);

  const visible = useMemo(() => {
    if (!detail) return [];
    return detail.entries.filter((e) => {
      if (sdrFilter !== "all" && e.sdrName !== sdrFilter) return false;
      if (search && !e.contactName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [detail, sdrFilter, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page]
  );

  if (!stage) {
    return (
      <section className="rounded-lg border border-dashed border-border-subtle bg-bg-card/50 p-8 text-center">
        <div className="mx-auto max-w-md space-y-1.5">
          <div className="text-sm font-medium text-text-secondary">Detalhes do funil</div>
          <p className="text-[13px] text-text-muted">
            Clique em qualquer etapa do funil acima para listar os contatos que estão nela —
            leads, conexões, reuniões agendadas ou realizadas.
          </p>
        </div>
      </section>
    );
  }

  const cfg = STAGE_CONFIG[stage];

  return (
    <section className="flex h-full flex-col overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
      <header className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
        <div className="space-y-1">
          <div
            className="text-[11px] font-medium uppercase tracking-[0.15em]"
            style={{ color: cfg.accent }}
          >
            Etapa do funil
          </div>
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">{cfg.title}</h2>
          <p className="text-[13px] text-text-muted">{cfg.subtitle}</p>
          {detail && (
            <p className="text-[13px] text-text-secondary">
              <span className="font-semibold text-text-primary">{detail.total}</span> contatos nesta etapa
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1.5 rounded-md border border-border-subtle px-3 py-1.5 text-xs font-medium text-text-muted transition hover:border-border-strong hover:text-text-primary"
        >
          <span>×</span>
          <span>Fechar</span>
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-6 py-3">
        <input
          type="search"
          placeholder="Buscar por nome do contato…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 flex-1 min-w-[220px] rounded-md border border-border-subtle bg-bg-primary px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-brand-amber focus:outline-none"
        />
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>SDR:</span>
          <select
            value={sdrFilter}
            onChange={(e) => setSdrFilter(e.target.value)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            {sdrOptions.map((s) => (
              <option key={s} value={s}>{s === "all" ? "Todos" : s}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto text-xs text-text-muted">
          {visible.length} de {detail?.total ?? 0}
        </div>
      </div>

      <div>
        {q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">Carregando etapa…</div>
        )}
        {detail && visible.length === 0 && !q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">
            Nenhum contato encontrado com os filtros atuais.
          </div>
        )}
        {visible.length > 0 && (
          <>
            <table className="w-full">
              <thead className="bg-bg-secondary">
                <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
                  <th className="px-5 py-3 text-left">Contato</th>
                  <th className="px-5 py-3 text-left">SDR</th>
                  <th className="px-5 py-3 text-left">{stageSpecificColumn(stage)}</th>
                  <th className="px-5 py-3 text-right">Dias parado</th>
                  <th className="px-5 py-3 text-left">Observação</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((entry, i) => (
                  <EntryRow
                    key={entry.contactId}
                    entry={entry}
                    stage={stage}
                    chip={cfg.chip}
                    isLast={i === pageItems.length - 1}
                  />
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <Paginator
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                rangeStart={(page - 1) * PAGE_SIZE + 1}
                rangeEnd={Math.min(page * PAGE_SIZE, visible.length)}
                total={visible.length}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

function stageSpecificColumn(stage: SdrStageKey): string {
  switch (stage) {
    case "leads": return "Entrou em";
    case "connections": return "Ligações feitas";
    case "meetingsScheduled": return "Reunião marcada";
    case "meetingsHeld": return "Closer que assumiu";
  }
}

function EntryRow({
  entry,
  stage,
  chip,
  isLast,
}: {
  entry: SdrStageEntry;
  stage: SdrStageKey;
  chip: string;
  isLast: boolean;
}) {
  const phoneFmt = entry.contactNumber.startsWith("55")
    ? `+55 (${entry.contactNumber.slice(2, 4)}) ${entry.contactNumber.slice(4, 9)}-${entry.contactNumber.slice(9)}`
    : entry.contactNumber;

  const specific =
    stage === "connections"
      ? `${entry.callsMade ?? 0} ligação(ões)`
      : stage === "meetingsScheduled" && entry.scheduledMeetingAt
        ? new Date(entry.scheduledMeetingAt).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
        : stage === "meetingsHeld"
          ? entry.closerName ?? "—"
          : new Date(entry.enteredStageAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });

  const daysChip =
    entry.daysInStage >= 7 ? "bg-danger/15 text-danger" : entry.daysInStage >= 3 ? "bg-warning/15 text-warning" : "bg-success/15 text-success";

  return (
    <tr
      className={cn(
        "text-sm transition hover:bg-bg-card-hover",
        !isLast && "border-b border-border-subtle"
      )}
    >
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-text-on-accent"
            style={{ backgroundColor: entry.avatarColor }}
          >
            {getInitials(entry.contactName)}
          </span>
          <div className="min-w-0">
            <div className="font-medium text-text-primary">{entry.contactName}</div>
            <div className="text-xs text-text-muted">{phoneFmt}</div>
            {entry.tags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {entry.tags.map((t) => (
                  <span
                    key={t.id}
                    className="inline-flex items-center gap-1 rounded-full bg-bg-primary px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                  >
                    <span className="h-1 w-1 rounded-full" style={{ backgroundColor: t.color }} />
                    {t.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-5 py-3 text-text-secondary">{entry.sdrName}</td>
      <td className="px-5 py-3">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold", chip)}>
          {specific}
        </span>
      </td>
      <td className="px-5 py-3 text-right">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold", daysChip)}>
          {entry.daysInStage}d
        </span>
      </td>
      <td className="px-5 py-3 text-text-muted italic">{entry.lastNote ?? "—"}</td>
    </tr>
  );
}

function Paginator({
  page,
  totalPages,
  onChange,
  rangeStart,
  rangeEnd,
  total,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
  rangeStart: number;
  rangeEnd: number;
  total: number;
}) {
  return (
    <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3">
      <span className="text-xs text-text-muted">
        Exibindo <span className="font-semibold text-text-primary">{rangeStart}–{rangeEnd}</span> de {total}
      </span>
      <div className="flex items-center gap-1">
        <PagerButton label="‹" disabled={page === 1} onClick={() => onChange(page - 1)} />
        {buildPageWindow(page, totalPages).map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="px-2 text-xs text-text-muted">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className={cn(
                "min-w-[30px] rounded-md px-2 py-1 text-xs font-medium transition",
                p === page
                  ? "bg-brand-amber text-text-on-accent"
                  : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
              )}
            >
              {p}
            </button>
          )
        )}
        <PagerButton label="›" disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </div>
    </div>
  );
}

function PagerButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md text-sm transition",
        disabled
          ? "cursor-not-allowed text-text-muted/40"
          : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
      )}
    >
      {label}
    </button>
  );
}

function buildPageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push("…");
  pages.push(total);
  return pages;
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
